import { Injectable } from '@angular/core';
import { TokenStorageService } from './token-storage.service';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private readonly STORAGE_KEY = 'SessionId';
  private readonly LOCALSTORAGE_SESSION_KEY = 'session-id'; // Same as TOKEN_KEY in TokenStorageService

  constructor(private tokenStorage: TokenStorageService) { }

  /**
   * Get or create session ID
   * 1. Check localStorage (persistent session)
   * 2. Check sessionStorage (chat-specific session)
   * 3. Generate new if none exists
   */
  getSessionId(): string {
    // 1️⃣ Check localStorage first
    let sessionId = localStorage.getItem(this.LOCALSTORAGE_SESSION_KEY);

    if (sessionId) {
      // Sync to sessionStorage for the current chat
      sessionStorage.setItem(this.STORAGE_KEY, sessionId);
      return sessionId;
    }

    // 2️⃣ Check sessionStorage next
    sessionId = sessionStorage.getItem(this.STORAGE_KEY);

    if (!sessionId) {
      // 3️⃣ If not found anywhere, generate new
      sessionId = this.generateRandomId();
      this.setSessionId(sessionId);
    }

    return sessionId;
  }

  /**
   * Explicitly set session ID in both storages
   */
  setSessionId(id: string): void {
    sessionStorage.setItem(this.STORAGE_KEY, id);
    localStorage.setItem(this.LOCALSTORAGE_SESSION_KEY, id);
  }

  /**
   * Initialize a website session ID on app start
   */
  initializeSession(): void {
    let websiteSessionId = localStorage.getItem(this.LOCALSTORAGE_SESSION_KEY);

    if (!websiteSessionId || this.isUserBasedSession(websiteSessionId)) {
      websiteSessionId = this.generateRandomId();
      localStorage.setItem(this.LOCALSTORAGE_SESSION_KEY, websiteSessionId);
    }

    sessionStorage.setItem(this.STORAGE_KEY, websiteSessionId);
  }

  /**
   * On login, generate a fresh session ID to avoid linking pre-login session with logged-in session
   */
  replaceWithUserIdIfLoggedIn(): void {
    this.generateNewSession();
  }

  /**
   * Clear all stored session IDs
   */
  clearSession(): void {
    sessionStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.LOCALSTORAGE_SESSION_KEY);
  }

  /**
   * Force a brand new session ID
   */
  generateNewSession(): string {
    const newSessionId = this.generateRandomId();
    this.setSessionId(newSessionId);
    return newSessionId;
  }

  /**
   * Check if there is any active session
   */
  hasActiveSession(): boolean {
    return !!this.getSessionId();
  }

  /**
   * Handle user logout → restore to anonymous session
   */
  onUserLogout(): void {
    const originalSessionId = localStorage.getItem(this.LOCALSTORAGE_SESSION_KEY);

    if (originalSessionId && !this.isUserBasedSession(originalSessionId)) {
      sessionStorage.setItem(this.STORAGE_KEY, originalSessionId);
    } else {
      const newWebsiteSession = this.generateRandomId();
      this.setSessionId(newWebsiteSession);
    }
  }

  /**
   * Detect if the session is a user-based numeric ID
   */
  private isUserBasedSession(sessionId: string): boolean {
    return !isNaN(Number(sessionId)) && sessionId.length < 10;
  }

  /**
   * Debug info
   */
  getSessionInfo(): {
    sessionId: string;
    source: 'localStorage' | 'sessionStorage' | 'generated';
    userId?: string;
  } {
    const localStorageId = localStorage.getItem(this.LOCALSTORAGE_SESSION_KEY);
    const sessionStorageId = sessionStorage.getItem(this.STORAGE_KEY);
    const userId = this.tokenStorage.getUserId();

    let source: 'localStorage' | 'sessionStorage' | 'generated' = 'generated';

    if (localStorageId) {
      source = 'localStorage';
    } else if (sessionStorageId) {
      source = 'sessionStorage';
    }

    return {
      sessionId: this.getSessionId(),
      source,
      userId: userId?.toString()
    };
  }

  /**
   * Generate a random anonymous session ID
   */
  private generateRandomId(): string {
    const chars = Math.random().toString(36).substring(2, 7);
    return `citizen-${chars}`;
  }
}
